import type { ReactElement } from 'react'
import { Route, Navigate, createRoutesFromElements, type RouteObject } from 'react-router-dom'
import { ProductTabRoute } from './ProductTabRoute'
import { LegacyRedirect } from './LegacyRedirect'
import { Home } from '../pages/Home'
import { NotFound } from '../pages/NotFound'
import { Subscription } from '../pages/Subscription'
import { AdditionalVolume } from '../pages/AdditionalVolume'
import { ExcessVolume } from '../pages/ExcessVolume'
import { Usage } from '../pages/Usage'
import { UserLimits } from '../pages/UserLimits'
import { MonthlyLimits } from '../pages/MonthlyLimits'
import { TenantLimits } from '../pages/TenantLimits'
import { Settings } from '../pages/Settings'
import { RequirePortalAdmin } from './RequirePortalAdmin'
import {
  DEFAULT_PRODUCT,
  LEGACY_REDIRECTS,
  PAGES,
  pagePath,
  type PageSlug,
  type RouteHandle,
} from '../lib/routes'

// Settings is not here: the generic mapper never builds an Admin-only page
// (see settingsRoute below).
type GenericPage = Exclude<PageSlug, 'settings'>

const PAGE_ELEMENTS: Record<GenericPage, ReactElement> = {
  subscription: <Subscription />,
  'additional-volume': <AdditionalVolume />,
  'excess-volume': <ExcessVolume />,
  'user-limits': <UserLimits />,
  'monthly-limits': <MonthlyLimits />,
  'tenant-limits': <TenantLimits />,
  usage: <Usage />,
}

/* Route scheme (MS-818; ARCHITECTURE_DECISIONS.md §40): clean paths, no `#`.
   - Tabbed page: `/{page}` replace-redirects to `/{page}/mambu-mcp`;
     `/{page}/:tab` renders the page (unknown tab → in-page NotFound); any
     deeper path (`/{page}/:tab/*`) → NotFound, like a bad tab.
   - Tab-less page (Monthly/Tenant Limits): `/{page}` only; `/{page}/*` →
     NotFound.
   - Legacy `/volume[/*]` and `/limits[/*]` replace-redirect to the current
     slug, keeping the rest of the path.
   - Anything else → NotFound (it used to redirect to Home). NotFound never
     redirects: the URL stays as typed (PO decision, OQ-5).
   - Settings (MS-997, Admin only): `/settings` replace-redirects to
     `/settings/access`, its one fixed tab; `/settings/*` anything else →
     NotFound. All of it behind RequirePortalAdmin, so a Controller lands on
     Home whatever the path (prototype `applyRoute`).
   Each route carries a RouteHandle (lib/routes.ts) — the breadcrumb is read
   from these matched handles, never by re-parsing the pathname.
   The two redirects stay render-time <Navigate replace> rather than loader
   `redirect()`s — see ARCHITECTURE_DECISIONS.md §40 for why. */
const HOME_HANDLE: RouteHandle = { home: true }
const pageHandle = (page: PageSlug): RouteHandle => ({ page })
const tabHandle = (page: PageSlug): RouteHandle => ({ page, tab: true })

function pageRouteElements() {
  const generic = (Object.keys(PAGES) as PageSlug[]).filter(
    (slug): slug is GenericPage => !PAGES[slug].adminOnly
  )
  return generic.map((slug) =>
    PAGES[slug].tabbed ? (
      <Route key={slug} path={`/${slug}`} handle={pageHandle(slug)}>
        <Route index element={<Navigate to={pagePath(slug, DEFAULT_PRODUCT)} replace />} />
        <Route
          path=":tab"
          handle={tabHandle(slug)}
          element={<ProductTabRoute page={slug}>{PAGE_ELEMENTS[slug]}</ProductTabRoute>}
        />
        <Route path=":tab/*" element={<NotFound page={slug} />} />
      </Route>
    ) : (
      <Route key={slug} path={`/${slug}`} handle={pageHandle(slug)}>
        <Route index element={PAGE_ELEMENTS[slug]} />
        <Route path="*" element={<NotFound page={slug} />} />
      </Route>
    )
  )
}

const SETTINGS_HANDLE: RouteHandle = { page: 'settings', adminOnly: true }
const SETTINGS_ACCESS_HANDLE: RouteHandle = { page: 'settings', adminOnly: true, fixedTab: true }

function settingsRoute() {
  return (
    <Route path="/settings" handle={SETTINGS_HANDLE} element={<RequirePortalAdmin />}>
      <Route index element={<Navigate to={pagePath('settings')} replace />} />
      <Route path="access" handle={SETTINGS_ACCESS_HANDLE} element={<Settings />} />
      <Route path="*" element={<NotFound page="settings" />} />
    </Route>
  )
}

/* The page routes on their own, without the AppShell wrapper — a module of
   their own (not routes.tsx) so component tests can mount real routing
   (params, redirects, history) around a page without also loading the
   shell and its auth/notifications dependencies (see
   __tests__/test-utils.tsx's renderAtRoute). routes.tsx's `appRoutes`
   composes them under <AppShell/>; nothing else differs between the two. */
export const pageRoutes: RouteObject[] = createRoutesFromElements(
  <>
    <Route path="/" element={<Home />} handle={HOME_HANDLE} />
    {pageRouteElements()}
    {settingsRoute()}
    {Object.entries(LEGACY_REDIRECTS).map(([legacy, to]) => (
      <Route key={legacy} path={`/${legacy}/*`} element={<LegacyRedirect to={to} />} />
    ))}
    <Route path="*" element={<NotFound />} />
  </>
)

